import React, { useEffect, useState, Fragment } from 'react';
import { toast } from 'react-toastify';
import { Link, useParams } from 'react-router-dom';
import api from '../../config/axios';
import Breadcrumb from '../common/breadcrumb';
import { date } from '../../utils/date';
import { generateReceiptHTML, getCurrencySymbol, formatAmount } from '../../utils/receipt-html';

// Cargar html2pdf.js desde CDN (evita problemas de compatibilidad con CRA/Babel)
const loadHtml2Pdf = () => {
    return new Promise((resolve, reject) => {
        if (window.html2pdf) {
            resolve(window.html2pdf);
            return;
        }
        const script = document.createElement('script');
        script.src = 'https://cdnjs.cloudflare.com/ajax/libs/html2pdf.js/0.10.2/html2pdf.bundle.min.js';
        script.onload = () => resolve(window.html2pdf);
        script.onerror = () => reject(new Error('No se pudo cargar html2pdf'));
        document.head.appendChild(script);
    });
};

const ReceiptDetail = () => {
    const { id } = useParams();
    const [receipt, setReceipt] = useState(null);
    const [loading, setLoading] = useState(true);
    const [sendingEmail, setSendingEmail] = useState(false);
    const [emailTo, setEmailTo] = useState('');
    const [generatingPdf, setGeneratingPdf] = useState(false);

    useEffect(() => {
        fetchReceipt();
    }, [id]);

    const fetchReceipt = async () => {
        try {
            const { data } = await api.get(`/receipt/${id}`);
            if (data.success) {
                setReceipt(data.data);
                if (data.data.email) {
                    setEmailTo(data.data.email);
                }
            }
        } catch (err) {
            toast.error('Error al cargar el recibo');
        } finally {
            setLoading(false);
        }
    };

    const handlePrint = () => {
        const printContent = generateReceiptHTML(receipt);
        const printWindow = window.open('', '_blank');
        printWindow.document.write(printContent);
        printWindow.document.close();
        printWindow.focus();
        printWindow.print();
    };

    const handleDownloadPdf = async () => {
        setGeneratingPdf(true);
        try {
            const html2pdf = await loadHtml2Pdf();
            const html = generateReceiptHTML(receipt);
            const container = document.createElement('div');
            container.style.position = 'absolute';
            container.style.left = '-9999px';
            container.style.top = '0';
            document.body.appendChild(container);
            container.innerHTML = html;

            const filename = `Recibo-${receipt.receiptNumber}-Aguaman.pdf`;
            const opt = {
                margin: 10,
                filename,
                image: { type: 'jpeg', quality: 0.98 },
                html2canvas: { scale: 2, useCORS: true },
                js2pdf: { unit: 'mm', format: 'letter', orientation: 'portrait' },
            };

            await html2pdf().set(opt).from(container.querySelector('.receipt-container')).save();
            document.body.removeChild(container);
            toast.success('PDF descargado');
        } catch (err) {
            console.error('PDF error:', err);
            toast.error('Error al generar el PDF');
        } finally {
            setGeneratingPdf(false);
        }
    };

    const handleShare = async () => {
        setGeneratingPdf(true);
        try {
            const html2pdf = await loadHtml2Pdf();
            const html = generateReceiptHTML(receipt);
            const container = document.createElement('div');
            container.style.position = 'absolute';
            container.style.left = '-9999px';
            container.style.top = '0';
            document.body.appendChild(container);
            container.innerHTML = html;

            const filename = `Recibo-${receipt.receiptNumber}-Aguaman.pdf`;
            const opt = {
                margin: 10,
                filename,
                image: { type: 'jpeg', quality: 0.98 },
                html2canvas: { scale: 2, useCORS: true },
                js2pdf: { unit: 'mm', format: 'letter', orientation: 'portrait' },
            };

            // Generar como blob para compartir
            const pdfBlob = await html2pdf()
                .set(opt)
                .from(container.querySelector('.receipt-container'))
                .outputPdf('blob');

            document.body.removeChild(container);

            const file = new File([pdfBlob], filename, { type: 'application/pdf' });

            if (navigator.canShare && navigator.canShare({ files: [file] })) {
                await navigator.share({
                    files: [file],
                    title: `Recibo #${receipt.receiptNumber} — Aguaman`,
                    text: `Recibo #${receipt.receiptNumber} - ${receipt.holderName}`,
                });
            } else {
                // Fallback: descargar el PDF
                const url = URL.createObjectURL(pdfBlob);
                const a = document.createElement('a');
                a.href = url;
                a.download = filename;
                a.click();
                URL.revokeObjectURL(url);
                toast.info('Descarga iniciada — tu navegador no soporta compartir');
            }
        } catch (err) {
            console.error('Share error:', err);
            if (err.name !== 'AbortError') {
                toast.error('Error al compartir');
            }
        } finally {
            setGeneratingPdf(false);
        }
    };

    const handleSendEmail = async () => {
        if (!emailTo || !emailTo.trim()) {
            toast.error('Ingrese un correo válido');
            return;
        }
        setSendingEmail(true);
        try {
            const { data } = await api.post(`/receipt/${id}/send-email`, { email: emailTo });
            if (data.success) {
                toast.success(`Recibo enviado a ${emailTo}`);
                fetchReceipt();
            }
        } catch (err) {
            toast.error('Error al enviar el correo');
        } finally {
            setSendingEmail(false);
        }
    };

    if (loading) {
        return (
            <div className="text-center py-5">
                <div className="spinner-border text-primary" role="status">
                    <span className="sr-only">Cargando...</span>
                </div>
            </div>
        );
    }

    if (!receipt) {
        return (
            <Fragment>
                <Breadcrumb title="Recibo no encontrado" />
                <div className="container-fluid">
                    <div className="text-center py-5">
                        <p style={{ fontSize: 18, color: '#777' }}>Recibo no encontrado</p>
                        <Link to={`${process.env.PUBLIC_URL}/recibos`} className="btn btn-primary mt-3">
                            Volver a recibos
                        </Link>
                    </div>
                </div>
            </Fragment>
        );
    }

    return (
        <Fragment>
            <Breadcrumb title={`Recibo #${receipt.receiptNumber}`} parent="Recibos" />
            <div className="container-fluid">
                <div className="row">
                    <div className="col-sm-12">
                        <div className="card">
                            <div className="card-header">
                                <div className="d-flex justify-content-between align-items-center">
                                    <h5>Recibo #{receipt.receiptNumber}</h5>
                                    <div>
                                        <Link
                                            to={`${process.env.PUBLIC_URL}/recibos`}
                                            className="btn btn-light btn-sm mr-2"
                                        >
                                            <i className="fa fa-arrow-left mr-1"></i>
                                            Volver
                                        </Link>
                                    </div>
                                </div>
                            </div>
                            <div className="card-body">
                                <div className="row mb-4">
                                    <div className="col-md-4">
                                        <div className="form-group">
                                            <label className="text-muted small">Titular</label>
                                            <p className="font-weight-bold" style={{ fontSize: 18 }}>
                                                {receipt.holderName}
                                            </p>
                                            {receipt.holderId && (
                                                <p className="text-muted">
                                                    <strong>Identificación:</strong> {receipt.holderId}
                                                </p>
                                            )}
                                        </div>
                                    </div>
                                    <div className="col-md-4">
                                        <div className="form-group">
                                            <label className="text-muted small">Fecha</label>
                                            <p>{date(receipt.createdAt)}</p>
                                        </div>
                                    </div>
                                    <div className="col-md-4">
                                        <div className="form-group">
                                            <label className="text-muted small">Franquicia</label>
                                            <p>{receipt.franchise ? receipt.franchise.name : '-'}</p>
                                        </div>
                                    </div>
                                </div>

                                {(receipt.holderPhone || receipt.holderAddress) && (
                                    <div className="row mb-4">
                                        {receipt.holderPhone && (
                                            <div className="col-md-3">
                                                <div className="form-group">
                                                    <label className="text-muted small">Teléfono</label>
                                                    <p>{receipt.holderPhone}</p>
                                                </div>
                                            </div>
                                        )}
                                        {receipt.holderAddress && (
                                            <div className="col-md-6">
                                                <div className="form-group">
                                                    <label className="text-muted small">Dirección</label>
                                                    <p>{receipt.holderAddress}</p>
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                )}

                                <div className="table-responsive">
                                    <table className="table table-bordered">
                                        <thead>
                                            <tr>
                                                <th>#</th>
                                                <th>Producto</th>
                                                <th className="text-center">Cantidad</th>
                                                <th className="text-right">Precio unit.</th>
                                                <th className="text-right">Subtotal</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {(receipt.items || []).map((item, i) => (
                                                <tr key={i}>
                                                    <td>{i + 1}</td>
                                                    <td>{item.name}</td>
                                                    <td className="text-center">{item.quantity}</td>
                                                    <td className="text-right">
                                                        {getCurrencySymbol(receipt.currency)}{' '}
                                                        {formatAmount(item.unitPrice, receipt.currency)}
                                                    </td>
                                                    <td className="text-right font-weight-bold">
                                                        {getCurrencySymbol(receipt.currency)}{' '}
                                                        {formatAmount(item.subtotal, receipt.currency)}
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                        <tfoot>
                                            <tr>
                                                <td colSpan="4" className="text-right font-weight-bold">
                                                    Total:
                                                </td>
                                                <td
                                                    className="text-right font-weight-bold"
                                                    style={{ fontSize: 18, color: '#00a4d3' }}
                                                >
                                                    {getCurrencySymbol(receipt.currency)}{' '}
                                                    {formatAmount(receipt.totalAmount, receipt.currency)}
                                                </td>
                                            </tr>
                                        </tfoot>
                                    </table>
                                </div>

                                {receipt.observation && (
                                    <div
                                        className="mt-3 p-3"
                                        style={{
                                            background: '#fffde8',
                                            borderLeft: '3px solid #ffc107',
                                            borderRadius: '4px',
                                        }}
                                    >
                                        <strong>Observación:</strong> {receipt.observation}
                                    </div>
                                )}

                                {receipt.paymentMethod && (
                                    <p className="mt-3">
                                        <strong>Método de pago:</strong> {receipt.paymentMethod}
                                    </p>
                                )}

                                <hr className="my-4" />

                                {/* Botones de acción */}
                                <div className="row mb-3">
                                    <div className="col-md-12">
                                        <div className="d-flex flex-wrap gap-2 mb-3">
                                            <button
                                                className="btn btn-outline-primary"
                                                onClick={handleDownloadPdf}
                                                disabled={generatingPdf}
                                            >
                                                {generatingPdf ? (
                                                    <span className="spinner-border spinner-border-sm mr-1" />
                                                ) : (
                                                    <i className="fa fa-download mr-1"></i>
                                                )}
                                                Descargar PDF
                                            </button>
                                            <button
                                                className="btn btn-outline-primary"
                                                onClick={handlePrint}
                                            >
                                                <i className="fa fa-print mr-1"></i>
                                                Imprimir
                                            </button>
                                            <button
                                                className="btn btn-outline-primary"
                                                onClick={handleShare}
                                                disabled={generatingPdf}
                                            >
                                                {generatingPdf ? (
                                                    <span className="spinner-border spinner-border-sm mr-1" />
                                                ) : (
                                                    <i className="fa fa-share-alt mr-1"></i>
                                                )}
                                                Compartir
                                            </button>
                                        </div>
                                    </div>
                                </div>

                                {/* Envío por correo */}
                                <div className="row">
                                    <div className="col-md-12">
                                        <div className="form-group">
                                            <label>Enviar por correo</label>
                                            <div className="input-group">
                                                <input
                                                    type="email"
                                                    className="form-control"
                                                    value={emailTo}
                                                    onChange={(e) => setEmailTo(e.target.value)}
                                                    placeholder="cliente@ejemplo.com"
                                                />
                                                <div className="input-group-append">
                                                    <button
                                                        className="btn btn-primary"
                                                        onClick={handleSendEmail}
                                                        disabled={sendingEmail}
                                                    >
                                                        {sendingEmail ? (
                                                            <span className="spinner-border spinner-border-sm" />
                                                        ) : (
                                                            <i className="fa fa-paper-plane mr-1"></i>
                                                        )}
                                                        {sendingEmail ? '' : ' Enviar'}
                                                    </button>
                                                </div>
                                            </div>
                                            {receipt.emailSentAt && (
                                                <small className="text-success mt-1 d-block">
                                                    <i className="fa fa-check"></i> Último envío:{' '}
                                                    {date(receipt.emailSentAt)}
                                                </small>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </Fragment>
    );
};

export default ReceiptDetail;
